import type {
  ShippingCreateBody, ShippingKind, ShippingRequest, ShippingUpdateBody,
} from '@/lib/apiClient';
import type { BadgeTone } from '@/components';
import { toDateInputValue } from '@/lib/dates';

/**
 * What the Shipping drawer holds and sends (#717, #718, #719). Pure, so the rules a request has to meet and
 * the body each save sends are tested without rendering anything.
 *
 * The API is the gate. Every rule here is a copy of one in `apps/api/src/shipping/lib/shipping-rules.ts`,
 * kept so a person hears about a gap before the round trip and not after. If the two ever disagree the
 * API wins, and the drawer shows whatever it says.
 */

export const KIND_LABELS: Record<ShippingKind, string> = { pickup: 'Pickup', delivery: 'Delivery' };
/** Written out, not built by adding an "s": "Delivery" + "s" is not a word. */
export const KIND_PLURALS: Record<ShippingKind, string> = { pickup: 'Pickups', delivery: 'Deliveries' };
export const KINDS: ShippingKind[] = ['pickup', 'delivery'];

/** What the drawer reports when it is done, so the screen that opened it can say what happened. */
export type ShippingChange = 'created' | 'updated' | 'deleted';

/** The line a screen shows after a change (null: nothing to say; the list already shows it). One copy, for the tab and the page. */
export const CHANGE_NOTICE: Record<ShippingChange, string | null> = {
  created: 'Request submitted. It is now in Logistics’ list.',
  deleted: 'Request deleted.',
  updated: null,
};

/** The jobsite can receive it: a third answer, "not answered", is a real state (the API stores null). */
export type SiteReadyChoice = '' | 'yes' | 'no';

export interface ShippingForm {
  kind: ShippingKind | '';
  items: string;
  fromLocation: string;
  toLocation: string;
  /** YYYY-MM-DD, or '' */
  neededByDate: string;
  expedited: boolean;
  dimensions: string;
  weight: string;
  /** A truck type id as the <select> holds it; '' = none. */
  truckTypeId: string;
  siteReady: SiteReadyChoice;
  contactName: string;
  contactPhone: string;
  notes: string;
  /** Only an existing request has a status to change; a new one starts as Submitted on the server. */
  statusId: string;
}

/** What a host can fill in before the drawer opens (for example, from a Materials row). */
export interface ShippingPrefill {
  kind?: ShippingKind;
  items?: string;
  fromLocation?: string;
  toLocation?: string;
  /** The Materials row the request is for. Its job must be the request's job; the server checks. */
  materialId?: number;
}

const siteReadyChoice = (v: boolean | null | undefined): SiteReadyChoice => (v === true ? 'yes' : v === false ? 'no' : '');

export function initialForm(item: ShippingRequest | null, prefill?: ShippingPrefill): ShippingForm {
  if (item) {
    return {
      kind: item.kind,
      items: item.items,
      fromLocation: item.fromLocation ?? '',
      toLocation: item.toLocation ?? '',
      neededByDate: toDateInputValue(item.neededByDate),
      expedited: item.expedited,
      dimensions: item.dimensions ?? '',
      weight: item.weight ?? '',
      truckTypeId: item.truckTypeId == null ? '' : String(item.truckTypeId),
      siteReady: siteReadyChoice(item.siteReady),
      contactName: item.contactName ?? '',
      contactPhone: item.contactPhone ?? '',
      notes: item.notes ?? '',
      statusId: String(item.statusId),
    };
  }
  return {
    kind: prefill?.kind ?? '',
    items: prefill?.items ?? '',
    fromLocation: prefill?.fromLocation ?? '',
    toLocation: prefill?.toLocation ?? '',
    neededByDate: '',
    expedited: false,
    dimensions: '',
    weight: '',
    truckTypeId: '',
    siteReady: '',
    contactName: '',
    contactPhone: '',
    notes: '',
    statusId: '',
  };
}

/** Trimmed text, with blank as null: a whitespace-only value is "no answer", not a value. */
const clean = (v: string): string | null => {
  const s = v.trim();
  return s === '' ? null : s;
};

/**
 * What a delivery TO A JOB is still missing, in the words the API's 400 uses. A pickup, and a delivery with no
 * job (to the shop or warehouse, where there is no onsite foreman to name), need none of it. "No" to "can the site
 * receive it" is an answer Logistics needs to hear; only a blank is missing.
 */
export function deliveryDetailsMissing(form: ShippingForm, hasJob: boolean): string[] {
  if (form.kind !== 'delivery' || !hasJob) return [];
  const missing: string[] = [];
  if (!clean(form.contactName)) missing.push('the onsite contact name');
  if (!clean(form.contactPhone)) missing.push('the onsite contact phone number');
  if (form.siteReady === '') missing.push('whether the jobsite can receive it');
  return missing;
}

/** Everything wrong with the form as it stands, one sentence each; empty when it can be sent. */
export function problems(form: ShippingForm, hasJob: boolean, isNew: boolean): string[] {
  const out: string[] = [];
  if (isNew && form.kind === '') out.push('Choose Pickup or Delivery.');
  if (!clean(form.items)) out.push('Say what is being moved.');
  const missing = deliveryDetailsMissing(form, hasJob);
  if (missing.length) out.push(`A delivery to a job needs ${missing.join(', ')}.`);
  return out;
}

const siteReadyValue = (c: SiteReadyChoice): boolean | undefined => (c === 'yes' ? true : c === 'no' ? false : undefined);

/**
 * The body of a NEW request. A blank optional field is left out, not sent as null. `kind` has to be chosen
 * (callers run `problems` first), so a missing one is a bug here, not a state to send.
 */
export function buildCreateBody(
  form: ShippingForm,
  ctx: { projectId?: number | null; materialId?: number | null },
): ShippingCreateBody {
  if (form.kind === '') throw new Error('A request needs a kind.');
  const body: ShippingCreateBody = { kind: form.kind, items: form.items.trim() };
  if (ctx.projectId != null) body.projectId = ctx.projectId;
  if (ctx.materialId != null) body.materialId = ctx.materialId;
  const text = (k: 'fromLocation' | 'toLocation' | 'dimensions' | 'weight' | 'contactName' | 'contactPhone' | 'notes') => {
    const v = clean(form[k]);
    if (v !== null) body[k] = v;
  };
  text('fromLocation'); text('toLocation'); text('dimensions'); text('weight'); text('contactName'); text('contactPhone'); text('notes');
  if (form.neededByDate) body.neededByDate = form.neededByDate;
  if (form.expedited) body.expedited = true;
  if (form.truckTypeId) body.truckTypeId = Number(form.truckTypeId);
  const ready = siteReadyValue(form.siteReady);
  if (ready !== undefined) body.siteReady = ready;
  return body;
}

/**
 * The body of an EDIT: only what the person changed. The API's PATCH is partial (an absent key leaves the stored
 * value alone), so sending the whole form would overwrite whatever someone else saved in between. A cleared field is
 * an explicit null; `{}` means nothing changed and nothing should be sent.
 */
export function buildUpdateBody(form: ShippingForm, item: ShippingRequest): ShippingUpdateBody {
  const now = initialForm(item);
  const body: ShippingUpdateBody = {};

  if (form.items.trim() !== now.items.trim()) body.items = form.items.trim();

  const text = (k: 'fromLocation' | 'toLocation' | 'dimensions' | 'weight' | 'contactName' | 'contactPhone' | 'notes') => {
    if ((clean(form[k]) ?? '') !== (clean(now[k]) ?? '')) body[k] = clean(form[k]);
  };
  text('fromLocation'); text('toLocation'); text('dimensions'); text('weight'); text('contactName'); text('contactPhone'); text('notes');

  if (form.neededByDate !== now.neededByDate) body.neededByDate = form.neededByDate || null;
  if (form.expedited !== now.expedited) body.expedited = form.expedited;
  if (form.truckTypeId !== now.truckTypeId) body.truckTypeId = form.truckTypeId ? Number(form.truckTypeId) : null;
  if (form.siteReady !== now.siteReady) body.siteReady = siteReadyValue(form.siteReady) ?? null;
  if (form.statusId !== now.statusId && form.statusId !== '') body.statusId = Number(form.statusId);
  return body;
}

/** A cached list with one request swapped for its saved copy (the rest, and the order, untouched). */
export function replaceRow(rows: ShippingRequest[], saved: ShippingRequest): ShippingRequest[] {
  return rows.map((r) => (r.id === saved.id ? saved : r));
}

/** A cached list without one request (a delete). */
export function withoutRow(rows: ShippingRequest[], id: number): ShippingRequest[] {
  return rows.filter((r) => r.id !== id);
}

/** The job a request is for, as people here name one: its number first. */
export function jobLabel(r: Pick<ShippingRequest, 'project' | 'jobNameRaw'>): string {
  if (r.project) return `#${r.project.jobNumber} — ${r.project.title}`;
  return r.jobNameRaw ?? 'No job';
}

/**
 * A badge tone per status, keyed by the stable `code`: the label is display text that gets reworded (#306), so
 * nothing here branches on it. An unknown code reads as neutral.
 */
export const STATUS_TONE: Record<string, BadgeTone> = {
  submitted: 'info',
  needs_info: 'warning',
  scheduled: 'success',
  completed: 'neutral',
  cancelled: 'neutral',
};

/** The message to show for a failed call: the API's own words when it sent any (a validation 400 sends a list). */
export function failureMessage(err: unknown): string {
  const e = err as { response?: { data?: { message?: string | string[] } }; message?: string } | null;
  const m = e?.response?.data?.message;
  if (Array.isArray(m)) return m.join(' ');
  return m ?? e?.message ?? 'Something went wrong.';
}
